import { dateToRoman, romanBreakdown, toRoman } from "./roman";

/**
 * Reading under the Roman numeral converter (B-0210-v2): the question headings people search for, each answered
 * in a few lines. Every numeral shown is worked out by the converter's own functions, so the page cannot disagree
 * with the tool. Not lazy-loaded: it is small and should be in the page from the first render.
 */
export default function RomanGuide() {
  return (
    <div className="mt-8 space-y-6 text-sm" data-testid="roman-guide">
      <section aria-labelledby="rg-how">
        <h2 id="rg-how" className="text-lg font-semibold mb-2">How to convert a number to Roman numerals</h2>
        <p className="text-muted-foreground mb-2">
          Seven letters do all the work: I = 1, V = 5, X = 10, L = 50, C = 100, D = 500 and M = 1,000. Split the number into thousands, hundreds, tens and ones,
          write each part, and join them. Six pairs are written by subtraction: IV = 4, IX = 9, XL = 40, XC = 90, CD = 400 and CM = 900.
        </p>
        <p className="text-muted-foreground">
          Example: 1994 = {romanBreakdown(1994)}, which is {toRoman(1994)}. And 2026 = {romanBreakdown(2026)}, which is {toRoman(2026)}.
          The converter above shows this working for any number you type.
        </p>
      </section>
      <section aria-labelledby="rg-why">
        <h2 id="rg-why" className="text-lg font-semibold mb-2">Why IIII and IC are wrong</h2>
        <p className="text-muted-foreground mb-2">
          Standard numerals never write I, X or C more than three times in a row, so 4 is {toRoman(4)}, not IIII. IIII is a clock-face tradition, so the
          converter reads it and says so, but the standard form is {toRoman(4)}.
        </p>
        <p className="text-muted-foreground">
          Only I (before V or X), X (before L or C) and C (before D or M) can be subtracted. So 99 is {toRoman(99)}, not IC; 49 is {toRoman(49)}, not IL;
          and 999 is {toRoman(999)}, not IM. V, L and D are never repeated or subtracted.
        </p>
      </section>
      <section aria-labelledby="rg-dates">
        <h2 id="rg-dates" className="text-lg font-semibold mb-2">Dates in Roman numerals (wedding, tattoo)</h2>
        <p className="text-muted-foreground">
          Convert the day, month and year one by one and join them with a separator. 14 June 2026 is {dateToRoman(14, 6, 2026, "dmy", ".")} (day.month.year), or {dateToRoman(14, 6, 2026, "mdy", ".")} in
          US order; 4 July 1776 is {dateToRoman(4, 7, 1776, "mdy", ".")}. Dots are the usual style, and a middle dot or slash is common on tattoos and certificates.
          Use the date boxes above; the year must be from 1 to 3,999.
        </p>
      </section>
    </div>
  );
}
